import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    IconButton,
    Box,
    TextField,
    Autocomplete,
    CircularProgress,
    RadioGroup,
    FormControlLabel,
    Radio,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useSnackbar } from "notistack";

import api from "../../api/axiosInstance";
import userService from "../../services/userService";

// Sends an in-app notification to one/several/all customers — automatically
// delivered as a push notification too, via Notification.js's post-save
// hook on the server (any device the customer has registered on My
// Waypoint). No separate "push" concept to configure here; creating the
// notification IS what triggers the push.
const SendNotificationDialog = ({ open, onClose, onSent }) => {
    const { enqueueSnackbar } = useSnackbar();

    const [audience, setAudience] = useState("selected"); // "selected" | "all"
    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");

    const [customerOptions, setCustomerOptions] = useState([]);
    const [customerLoading, setCustomerLoading] = useState(false);
    const [customerInput, setCustomerInput] = useState("");
    const [selectedCustomers, setSelectedCustomers] = useState([]);

    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (!open) {
            setAudience("selected");
            setTitle("");
            setMessage("");
            setCustomerInput("");
            setCustomerOptions([]);
            setSelectedCustomers([]);
        }
    }, [open]);

    // Same 1s debounce pattern used by UnclaimedPackageDialog's customer
    // search — avoids firing a query on every keystroke.
    useEffect(() => {
        if (!open) return;
        const query = customerInput.trim();
        if (query.length < 2) {
            setCustomerOptions([]);
            return;
        }
        setCustomerLoading(true);
        const timer = setTimeout(() => {
            userService
                .getAll({ role: "customer", search: query, limit: 20 })
                .then(({ data }) => setCustomerOptions(data.data || []))
                .catch(() => setCustomerOptions([]))
                .finally(() => setCustomerLoading(false));
        }, 1000);
        return () => clearTimeout(timer);
    }, [customerInput, open]);

    const canSend =
        title.trim() &&
        message.trim() &&
        (audience === "all" || selectedCustomers.length > 0);

    const handleSend = async () => {
        setSending(true);
        try {
            const { data } = await api.post("/notifications/broadcast", {
                title: title.trim(),
                message: message.trim(),
                sendToAll: audience === "all",
                recipientIds: audience === "selected" ? selectedCustomers.map((c) => c._id) : undefined,
            });
            enqueueSnackbar(data.message || `Sent to ${data.data.sentCount} customer(s)`, { variant: "success" });
            onSent?.();
            onClose();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to send notification", { variant: "error" });
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography fontWeight={700}>Send Notification</Typography>
                <IconButton size="small" onClick={onClose}>
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <TextField label="Title" fullWidth value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
                    <TextField
                        label="Message"
                        fullWidth
                        multiline
                        minRows={3}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                    />

                    <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            SEND TO
                        </Typography>
                        <RadioGroup row value={audience} onChange={(e) => setAudience(e.target.value)}>
                            <FormControlLabel value="selected" control={<Radio size="small" />} label="Specific customers" />
                            <FormControlLabel value="all" control={<Radio size="small" />} label="All customers" />
                        </RadioGroup>
                    </Box>

                    {audience === "selected" && (
                        <Autocomplete
                            multiple
                            options={customerOptions}
                            loading={customerLoading}
                            value={selectedCustomers}
                            onChange={(_, val) => setSelectedCustomers(val)}
                            inputValue={customerInput}
                            onInputChange={(_, val) => setCustomerInput(val)}
                            filterOptions={(x) => x}
                            getOptionLabel={(o) => `${o.firstName} ${o.lastName} (${o.email})`}
                            isOptionEqualToValue={(o, v) => o._id === v._id}
                            noOptionsText={
                                customerInput.trim().length < 2 ? "Type at least 2 characters…" : "No customers found"
                            }
                            renderInput={(params) => (
                                <TextField {...params} label="Customers" placeholder="Search by name or email…" />
                            )}
                        />
                    )}

                    {audience === "all" && (
                        <Typography variant="caption" color="text.secondary">
                            Sends to every active customer account — this can't be undone once sent.
                        </Typography>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} variant="outlined">
                    Cancel
                </Button>
                <Button onClick={handleSend} variant="contained" disabled={!canSend || sending}>
                    {sending ? <CircularProgress size={18} color="inherit" /> : "Send"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SendNotificationDialog;
